import React, { useEffect, useRef, useState } from "react";
import TornaAlMenu from "../components/TornaAlMenu";

const API_URL = process.env.REACT_APP_API_URL || "";

const mediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("blob:")) return url;
  return `${API_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export default function AppBetaPage() {
  const [title, setTitle] = useState("");
  const [area, setArea] = useState("");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [communications, setCommunications] = useState([]);
  const [selectedCommunication, setSelectedCommunication] = useState(null);
  const [answers, setAnswers] = useState({});
  const [ticketReply, setTicketReply] = useState("");
  const [ticketImage, setTicketImage] = useState(null);
  const chatBoxRef = useRef(null);

  const token = localStorage.getItem("access");

  const loadBeta = async () => {
    if (!token) return;

    try {
      const [ticketsRes, commRes] = await Promise.all([
        fetch(`${API_URL}/api/app/beta/bugs/mine/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_URL}/api/app/beta/communications/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

        if (ticketsRes.ok) {
          const ticketsData = await ticketsRes.json();
          setTickets(ticketsData);
          setSelectedTicket((current) => {
            if (!current) return current;
            return ticketsData.find((x) => x.id === current.id) || current;
          });
        }
      if (commRes.ok) {
        const commData = await commRes.json();
        setCommunications(
          (Array.isArray(commData) ? commData : []).filter(
            (c) => !(Array.isArray(c.questions_json) && c.questions_json.length > 0)
          )
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadBeta();
    const timer = setInterval(loadBeta, 10000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTicket) return;
    const box = chatBoxRef.current;
    if (!box) return;
    setTimeout(() => {
      box.scrollTop = box.scrollHeight;
    }, 100);
  }, [selectedTicket?.id, selectedTicket?.messages?.length]);

  const markTicketSeen = async (ticketId) => {
    try {
      await fetch(`${API_URL}/api/app/beta/bugs/${ticketId}/seen/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadBeta();
    } catch (e) {
      console.error(e);
    }
  };

  const sendTyping = async (ticketId) => {
    try {
      await fetch(`${API_URL}/api/app/beta/bugs/${ticketId}/typing/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    markTicketSeen(ticket.id);
  };

  const sendCommunicationAnswer = async (communicationId) => {
    setMsg("");
    setErr("");

    const current = answers[communicationId] || {};
    const answersJson = current.answers_json || {};
    const comment = current.comment || "";

    try {
      const res = await fetch(`${API_URL}/api/app/beta/communications/${communicationId}/answer/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          answer: "OK",
          answers_json: answersJson,
          comment,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore invio risposta.");

      setMsg("✅ Grazie! Il sondaggio è stato inviato correttamente.");
      setSelectedCommunication(null);
      loadBeta();
    } catch (e) {
      setErr(e.message || "Errore invio risposta.");
    }
  };

  const setQuestionAnswer = (communicationId, questionIndex, value) => {
    const current = answers[communicationId] || {};
    setAnswers({
      ...answers,
      [communicationId]: {
        ...current,
        answers_json: {
          ...(current.answers_json || {}),
          [questionIndex]: value,
        },
      },
    });
  };

  const sendTicketReply = async () => {
    if (!selectedTicket) return;

    const text = ticketReply.trim();
    if (!text && !ticketImage) {
      setErr("Scrivi un messaggio o allega un'immagine.");
      return;
    }

    const fd = new FormData();
    fd.append("message", text);
    if (ticketImage) fd.append("image", ticketImage);

    setErr("");
    setMsg("");

    try {
        const res = await fetch(`${API_URL}/api/app/beta/bugs/${selectedTicket.id}/messages/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: fd,
        });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore invio messaggio.");

      if (data?.report) {
        setSelectedTicket(data.report);
        setTickets((prev) => prev.map((x) => x.id === data.report.id ? data.report : x));
      }

      setTicketReply("");
      setMsg("✅ Messaggio inviato.");
      await loadBeta();
    } catch (e) {
      setErr(e.message || "Errore invio messaggio.");
    }
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    setMsg("");
    setErr("");

    if (!title.trim()) {
      setErr("Inserisci un titolo per la segnalazione.");
      return;
    }
    if (!description.trim()) {
      setErr("Descrivi cosa non funziona.");
      return;
    }

    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("area", area.trim());
    fd.append("description", description.trim());
    fd.append("page_url", window.location.pathname);

    Array.from(screenshots || []).slice(0, 5).forEach((file) => {
      fd.append("screenshots", file);
    });

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/app/beta/bugs/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Errore invio segnalazione.");

      if (title === "Richiesta eliminazione account") {
        setMsg(`✅ Richiesta inviata correttamente.

La tua richiesta sarà presa in carico dal punto vendita di riferimento nel più breve tempo possibile.

Prima della chiusura definitiva ti consigliamo di utilizzare l'eventuale credito disponibile nel Wallet, gli eventuali bonus promozionali e i punti fedeltà già convertibili in premio.

Riceverai una conferma una volta completata la procedura.

Ricorda che, dopo la chiusura definitiva, non sarà più possibile utilizzare l'App, il Wallet, i punti fedeltà, gli eventuali bonus e tutti gli altri vantaggi associati all'account.`);
      } else {
        setMsg("✅ Ticket inviato correttamente. Lo trovi sotto nella sezione I tuoi ticket.");
      }
        if (data?.report) {
          setTickets((prev) => [data.report, ...prev.filter((x) => x.id !== data.report.id)]);
        }
        setTitle("");
        setArea("");
        setDescription("");
        setScreenshots([]);
        const input = document.getElementById("beta-screenshots");
        if (input) input.value = "";
        await loadBeta();
        window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(e.message || "Errore invio segnalazione.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container py-4">
      <TornaAlMenu />

      <div
        className="rounded-5 p-4 mb-4 text-white"
        style={{
          background: "linear-gradient(135deg, #5b0f1b 0%, #8b1e2d 55%, #211014 100%)",
          boxShadow: "0 18px 40px rgba(91,15,27,0.22)",
        }}
      >
        <div className="small text-uppercase opacity-75 mb-2">Assistenza clienti</div>
        <h1 className="fw-bold mb-2">🎧 Centro Assistenza</h1>
        <p className="mb-0 opacity-75">
          Apri un ticket, allega screenshot e controlla qui le risposte dell'assistenza.
        </p>
      </div>

      {msg && (
        <div className="alert alert-success rounded-4 shadow-sm fw-bold p-4" style={{ fontSize: 18 }}>
          {msg}
        </div>
      )}
      {err && <div className="alert alert-danger rounded-4">{err}</div>}

      <div className="row g-3">
        <div className="col-12 col-lg-6">
          <div className="card border-0 rounded-5 h-100 shadow-sm">
            <div className="card-body p-4">
              <div style={{ fontSize: 46 }}>🐞</div>
              <h3 className="fw-bold mt-2">Apri un ticket</h3>
              <p className="text-muted">
                Raccontaci cosa non funziona e allega uno screenshot se può essere utile.
              </p>

              <form onSubmit={submitTicket}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Titolo</label>
                  <input
                    className="form-control rounded-4"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Esempio: il carrello non si apre"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Dove è successo?</label>
                  <input
                    className="form-control rounded-4"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    placeholder="Esempio: Offerte, Wallet, Checkout..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Descrizione</label>
                  <textarea
                    className="form-control rounded-4"
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Spiegaci cosa è successo, cosa stavi facendo e cosa ti aspettavi."
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">Screenshot</label>
                  <input
                    id="beta-screenshots"
                    type="file"
                    className="form-control rounded-4"
                    accept="image/*"
                    multiple
                    onChange={(e) => setScreenshots(e.target.files)}
                  />
                  <div className="small text-muted mt-2">Puoi allegare fino a 5 immagini.</div>
                </div>

                <button
                  className="btn btn-danger rounded-pill fw-bold px-4 py-3 w-100"
                  disabled={sending}
                >
                  {sending ? "Invio in corso..." : "Invia richiesta"}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card border-0 rounded-5 shadow-sm">
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">I tuoi ticket</h4>

              {tickets.length === 0 ? (
                <div className="alert alert-light rounded-4 mb-0">
                  Non hai ancora aperto ticket.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {tickets.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      className="border rounded-4 p-3 text-start bg-white w-100"
                      onClick={() => openTicket(b)}
                    >
                      <div className="d-flex justify-content-between gap-2">
                        <div>
                          <div className="fw-bold">{b.title}</div>
                          <div className="small text-muted">{b.area || "Area non indicata"}</div>
                          {b.reporter_first_name && (
                            <div className="small text-muted">Segnalato da: {b.reporter_first_name}</div>
                          )}
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                          <span className="badge bg-secondary align-self-start">
                            {b.status_label || b.status}
                          </span>
                          {Number(b.client_unread_count || 0) > 0 && (
                            <span className="badge text-bg-danger rounded-pill">
                              💬 {b.client_unread_count} nuovo
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-muted small mt-2 mb-1">{b.description}</p>
                      <div className="small fw-semibold">Apri ticket →</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card border-0 rounded-5 h-100 shadow-sm mb-3">
            <div className="card-body p-4">
              <div style={{ fontSize: 40 }}>📢</div>
              <h3 className="fw-bold mt-2">Comunicazioni dal negozio</h3>
              <p className="text-muted">
                Qui troverai avvisi, aggiornamenti e comunicazioni dal negozio.
              </p>

              {communications.length === 0 ? (
                <div className="alert alert-light rounded-4 mb-0">
                  Nessuna comunicazione al momento.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                    {communications.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="border rounded-4 p-3 text-start bg-white w-100"
                        onClick={() => setSelectedCommunication(c)}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div>
                            <h5 className="fw-bold mb-1">🧪 {c.title}</h5>
                            <div className="small text-muted">
                              {(c.questions_json || []).length} domande
                            </div>
                          </div>
                          <span className={`badge rounded-pill ${c.my_response ? "text-bg-success" : "text-bg-warning"}`}>
                            {c.my_response ? "Completata" : "Da completare"}
                          </span>
                        </div>
                        <div className="small fw-semibold mt-2">Apri missione →</div>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

        {selectedTicket && (
          <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content rounded-5 border-0">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold mb-1">🎫 {selectedTicket.title}</h5>
                    <div className="text-muted small">
                      {selectedTicket.area || "Area non indicata"}
                    </div>
                    {selectedTicket.reporter_first_name && (
                      <div className="text-muted small">
                        Segnalato da: {selectedTicket.reporter_first_name}
                      </div>
                    )}
                  </div>
                  <button type="button" className="btn-close" onClick={() => { setSelectedTicket(null); setTicketReply(""); }}></button>
                </div>

                <div className="modal-body">
                  <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                    <span className="badge bg-secondary">
                      Stato: {selectedTicket.status_label || selectedTicket.status}
                    </span>
                    {selectedTicket.reporter_first_name && (
                      <span className="badge bg-light text-dark">
                        Segnalato da: {selectedTicket.reporter_first_name}
                      </span>
                    )}
                  </div>

                  <div className="fw-bold mb-2">Segnalazione inviata</div>
                  <p className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
                    {selectedTicket.description || "Nessuna descrizione inserita."}
                  </p>

                  {(selectedTicket.admin_note || selectedTicket.resolution_note) && (
                    <div className="alert alert-light rounded-4 mt-3 mb-0">
                      <div className="fw-bold mb-2">Aggiornamento dal gestionale</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {selectedTicket.admin_note || selectedTicket.resolution_note}
                      </div>
                    </div>
                  )}

                    <hr className="my-4" />

                    <h6 className="fw-bold mb-3">💬 Conversazione</h6>

                    <div ref={chatBoxRef} className="border rounded-4 p-3 mb-3" style={{ maxHeight: 320, overflowY: "auto", background: "#fafafa" }}>
                      {(selectedTicket.messages || []).length === 0 ? (
                        <div className="text-muted small">Nessun messaggio.</div>
                      ) : (
                        selectedTicket.messages.map((m) => (
                          <div key={m.id} className={`mb-3 d-flex ${m.sender_type === "CLIENT" ? "justify-content-end" : "justify-content-start"}`}>
                            <div className={`rounded-4 px-3 py-2 ${m.sender_type === "CLIENT" ? "bg-danger text-white" : "bg-light"}`} style={{ maxWidth: "80%" }}>
                              <div className="small fw-bold mb-1" style={{ opacity: 0.8 }}>{m.sender_name}</div>
                                {m.message && <div style={{ whiteSpace: "pre-wrap" }}>{m.message}</div>}
                                {m.image_url && (
                                  <a href={mediaUrl(m.image_url)} target="_blank" rel="noreferrer" className="d-block mt-2">
                                    <img src={mediaUrl(m.image_url)} alt="Allegato messaggio" className="img-fluid rounded-3 border" style={{ maxHeight: 220 }} />
                                  </a>
                                )}
                              <div className="small mt-2" style={{ opacity: 0.7 }}>{m.created_at ? new Date(m.created_at).toLocaleString("it-IT") : ""}</div>
                            </div>
                          </div>
                        ))
                      )}
                        {selectedTicket.support_is_typing && (
                          <div className="small text-muted fst-italic mt-2">
                            🍷 Dallo Zio Roby sta scrivendo...
                          </div>
                        )}
                    </div>

                      {selectedTicket.status === "RESOLVED" ? (
                        <div className="alert alert-success rounded-4 mb-0 fw-semibold">
                          ✅ Il tuo ticket è stato risolto.

Se dovessi avere bisogno di ulteriore assistenza, puoi sempre aprire una nuova segnalazione.
                        </div>
                      ) : (
                        <>
                          <textarea
                            className="form-control rounded-4 mb-3"
                            rows={3}
                            placeholder="Scrivi un messaggio..."
                            value={ticketReply}
                              onChange={(e) => {
                                setTicketReply(e.target.value);
                                if (selectedTicket) sendTyping(selectedTicket.id);
                              }}
                          />

                          <input
                            id="ticket-reply-image"
                            type="file"
                            accept="image/*"
                            className="form-control rounded-4 mb-3"
                            onChange={(e) => setTicketImage(e.target.files?.[0] || null)}
                          />

                            {ticketImage && (
                              <div className="alert alert-light rounded-4 d-flex align-items-center justify-content-between gap-3 mb-3">
                                <div className="small">
                                  <div className="d-flex align-items-center gap-3">
                                    <img
                                      src={URL.createObjectURL(ticketImage)}
                                      alt="Anteprima allegato"
                                      className="rounded-3 border"
                                      style={{ width: 72, height: 72, objectFit: "cover" }}
                                    />
                                    <div>
                                      <div className="fw-semibold">Immagine selezionata</div>
                                      <div className="text-muted">{ticketImage.name}</div>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    setTicketImage(null);
                                    const input = document.getElementById("ticket-reply-image");
                                    if (input) input.value = "";
                                  }}
                                >
                                  Rimuovi
                                </button>
                              </div>
                            )}

                          <button type="button" className="btn btn-danger rounded-pill w-100 fw-bold" onClick={sendTicketReply}>
                            Invia messaggio
                          </button>
                        </>
                      )}
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedCommunication && (
          <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.45)" }}>
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content rounded-5 border-0">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title fw-bold mb-1">🧪 {selectedCommunication.title}</h5>
                    <div className="text-muted small">
                      {(selectedCommunication.questions_json || []).length} domande
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setSelectedCommunication(null)}></button>
                </div>

                <div className="modal-body">
                  <p className="text-muted" style={{ whiteSpace: "pre-wrap" }}>{selectedCommunication.message}</p>

                  {(selectedCommunication.questions_json || []).length > 0 && (
                    <div className="mt-3">
                      <div className="fw-bold mb-3">📋 Sondaggio</div>

                      {selectedCommunication.my_response ? (
                        <div className="alert alert-success rounded-4 py-2 mb-0">
                          <div className="fw-semibold mb-2">Risposte inviate</div>
                          {(selectedCommunication.questions_json || []).map((q, qIndex) => (
                            <div key={qIndex} className="small mb-2">
                              <div className="fw-semibold">{q.text}</div>
                              <div>{selectedCommunication.my_response.answers_json?.[qIndex] || "-"}</div>
                            </div>
                          ))}
                          {selectedCommunication.my_response.comment && (
                            <div className="small mt-2">
                              <strong>Commento:</strong> {selectedCommunication.my_response.comment}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {(selectedCommunication.questions_json || []).map((q, qIndex) => (
                            <div key={qIndex} className="mb-3">
                              <div className="small fw-semibold mb-2">{q.text}</div>
                              <div className="d-flex flex-column gap-2">
                                {(q.options || []).map((opt, optIndex) => (
                                  <label key={optIndex} className="border rounded-4 px-3 py-2">
                                    <input
                                      type="radio"
                                      className="form-check-input me-2"
                                      name={`beta-${selectedCommunication.id}-q-${qIndex}`}
                                      checked={answers[selectedCommunication.id]?.answers_json?.[qIndex] === opt}
                                      onChange={() => setQuestionAnswer(selectedCommunication.id, qIndex, opt)}
                                    />
                                    {opt}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}

                          <textarea
                            className="form-control rounded-4 mb-3"
                            rows={2}
                            value={answers[selectedCommunication.id]?.comment || ""}
                            onChange={(e) =>
                              setAnswers({
                                ...answers,
                                [selectedCommunication.id]: {
                                  ...(answers[selectedCommunication.id] || {}),
                                  comment: e.target.value,
                                },
                              })
                            }
                            placeholder="Commento facoltativo..."
                          />

                          <button
                            type="button"
                            className="btn btn-success rounded-pill w-100 fw-bold"
                            onClick={() => sendCommunicationAnswer(selectedCommunication.id)}
                          >
                            Invia sondaggio
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
}

